<template>
  <!-- badge: resumen para tablas -->
  <span v-if="mode == 'badge'">
    <span v-if="!current" class="text-muted">{{emptyLabel}}</span>
    <span v-else class="monitor-cost-cell" v-b-tooltip.hover.html.left="{ title: tooltip, boundary: 'window' }">
      <span>
        <b>{{money(current.monthly, current.currency)}} <small class="text-muted">/ mes</small></b>
        <span v-if="!manual" class="monitor-cost-cell__tag">Sugerido</span>
        <AppIcon v-if="difference || warnings.length" name="warning" class="monitor-cost-cell__warn" />
      </span>
      <small v-for="item in periods.slice(1)" :key="item.months" class="text-muted">{{money(item.amount, current.currency)}} a {{item.label}}</small>
    </span>
  </span>

  <!-- detail: desglose completo -->
  <div v-else>
    <div v-if="!current" class="text-muted">{{emptyLabel}}</div>
    <div v-else class="monitor-detail">
      <div class="monitor-detail__tiles monitor-cost-tiles">
        <div v-for="item in periods" :key="item.months" class="monitor-detail__tile">
          <span class="monitor-detail__tile-label">A {{item.label}}</span>
          <span class="monitor-detail__tile-value">{{money(item.amount, current.currency)}}</span>
        </div>
      </div>
      <div class="monitor-detail__row">
        <span class="monitor-detail__label"><AppIcon name="autorenew" /> Frecuencia</span>
        <span class="monitor-detail__value">{{frequency}} <small class="text-muted">(según el servicio)</small></span>
      </div>
      <div class="monitor-detail__row">
        <span class="monitor-detail__label"><AppIcon name="edit" /> Costo cargado</span>
        <span class="monitor-detail__value">
          <template v-if="manual">
            {{money(manual.price, manual.currency)}}
            <small v-if="manual.updated_at" class="text-muted">· actualizado el {{formatDate(manual.updated_at, false)}}</small>
          </template>
          <span v-else class="monitor-pill monitor-pill--warning">Sin cargar, se muestra el sugerido</span>
        </span>
      </div>
      <div class="monitor-detail__row monitor-detail__row--stack">
        <span class="monitor-detail__label"><AppIcon name="lightbulb" /> Sugerido</span>
        <span class="monitor-detail__value">
          <template v-if="suggested">
            {{money(suggested.period, suggested.currency)}} <small class="text-muted">({{money(suggested.monthly, suggested.currency)}} / mes)</small>
            <small class="d-block text-muted">
              Según la última renovación: {{money(suggested.price, suggested.currency)}} del {{formatDate(suggested.date_init, false)}} al {{formatDate(suggested.until || suggested.date_end, false)}}
              ({{suggested.months == 1 ? '1 mes' : suggested.months + ' meses'}})
            </small>
          </template>
          <span v-else class="text-muted">El servicio no tiene renovaciones</span>
        </span>
      </div>
      <div v-for="(warning, index) in allWarnings" :key="index" class="monitor-detail__footer text-warning">
        <AppIcon name="warning" /> {{warning}}
      </div>
    </div>
  </div>
</template>

<script>
  // cost del servidor (lo calcula el backend):
  // {months (frecuencia del servicio), manual: {price, currency, updated_at, monthly, period, annual}|null,
  //  suggested: {activity_id, date_init, date_end, until, months, price, currency, monthly, period, annual}|null,
  //  warnings: [] (datos que no cierran entre el servicio y su última renovación)}
  import { formatDate, costFrequencyLabel } from './monitorConfig'
  import './monitorDetail.css'

  export default {
    props: {
      value: { default: null },
      // tiene servicio asignado (sin renovaciones se aclara; sin servicio no hay costo)
      hasService: { type: Boolean, default: false },
      mode: { type: String, default: 'badge' },
    },
    computed: {
      cost() {
        return this.value && typeof this.value == 'object' && this.value.months ? this.value : null
      },
      manual() {
        return this.cost ? this.cost.manual : null
      },
      suggested() {
        return this.cost ? this.cost.suggested : null
      },
      warnings() {
        return this.cost && Array.isArray(this.cost.warnings) ? this.cost.warnings : []
      },
      allWarnings() {
        return this.difference ? this.warnings.concat([this.difference]) : this.warnings
      },
      // el que se muestra: el cargado y, si no hay, el sugerido
      current() {
        return this.manual || this.suggested
      },
      emptyLabel() {
        return this.hasService ? 'Sin costo' : '—'
      },
      frequency() {
        return this.cost ? costFrequencyLabel(this.cost.months) : ''
      },
      // importes a 1 mes, al período del servicio (si no es mensual ni anual) y a 12 meses
      periods() {
        if(!this.current) return []
        var months = [1]
        if(this.cost.months != 1 && this.cost.months != 12) months.push(this.cost.months)
        months.push(12)
        return months.map(count => ({
          months: count,
          label: count == 1 ? '1 mes' : count + ' meses',
          amount: count == 12 ? this.current.annual : (count == this.cost.months ? this.current.period : this.current.monthly),
        }))
      },
      // el cargado difiere más de un 1 % del sugerido (misma moneda)
      difference() {
        if(!this.manual || !this.suggested || this.manual.currency != this.suggested.currency) return ''
        var base = this.suggested.monthly
        var diff = this.manual.monthly - base
        if(!base || Math.abs(diff) / base <= 0.01) return ''
        var percent = Math.round(Math.abs(diff) / base * 100)
        return 'El costo cargado es un ' + percent + ' % ' + (diff > 0 ? 'mayor' : 'menor') + ' que la última renovación (' +
          this.money(base, this.suggested.currency) + ' / mes)'
      },
      tooltip() {
        if(!this.current) return ''
        var lines = ['<b>Frecuencia:</b> ' + this.frequency + ' (según el servicio)']
        if(this.manual) {
          lines.push('<b>Cargado:</b> ' + this.money(this.manual.price, this.manual.currency) + (this.manual.updated_at ? ' · ' + formatDate(this.manual.updated_at, false) : ''))
        } else {
          lines.push('<b>Sin costo cargado:</b> se muestra el sugerido')
        }
        if(this.suggested) {
          lines.push('<b>Sugerido:</b> ' + this.money(this.suggested.period, this.suggested.currency) +
            ' (renovación del ' + formatDate(this.suggested.date_init, false) + ' al ' + formatDate(this.suggested.until || this.suggested.date_end, false) + ')')
        }
        this.allWarnings.forEach(warning => lines.push('⚠ ' + this.escape(warning)))
        return lines.join('<br>')
      },
    },
    methods: {
      formatDate,
      escape(text) {
        return String(text || '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c])
      },
      money(value, currency) {
        return Intl.NumberFormat('es-AR', { style: 'currency', currency: currency || 'ARS' }).format(value || 0)
      },
    }
  }
</script>

<style scoped>
  .monitor-cost-cell {
    display: inline-flex;
    flex-direction: column;
    white-space: nowrap;
    cursor: default;
    line-height: 1.3;
  }

  .monitor-cost-cell__tag {
    margin-left: 4px;
    padding: 0 6px;
    border-radius: 999px;
    font-size: 10px;
    font-weight: 600;
    line-height: 16px;
    background: var(--app-color-warning-soft);
    color: var(--app-color-warning-hover);
  }

  .monitor-cost-cell__warn {
    margin-left: 4px;
    color: var(--app-color-warning-hover);
  }

  .monitor-cost-tiles {
    grid-template-columns: repeat(auto-fit, minmax(90px, 1fr));
  }
</style>
